import { formatDateByPattern, formatWallClockHourLocalized, getIsoWeek } from '@/utils/date';
import { getChartLabelDateFormat, type Locale } from '@/utils/localeFormatters';

import type { ChartTheme } from '../theme';

import type { MetricXAxisKind, MetricXTicks } from './axisTypes';
import {
    CHART_AXIS_BORDER_MARGIN,
    CHART_GRID_DASH_PATTERN,
    CHART_GRID_LINE_WIDTH,
} from './constants';
import type { MetricXScaleOptions } from './optionTypes';

/**
 * The x axis of a metric chart: its scale, and the week formatting its tooltip shares. Split
 * out of `buildChartOptions` to keep that file about the value axes and the tooltip.
 */

/**
 * The Sunday closing an ISO week that starts on `mondayIso`.
 *
 * Plain UTC arithmetic on the calendar date. The keys are date-only strings with no
 * zone, so adding six days cannot cross a DST boundary the way a local-time Date could.
 */
function isoWeekEnd(mondayIso: string): string {
    const parsed = Date.parse(mondayIso);
    if (Number.isNaN(parsed)) return mondayIso;
    return new Date(parsed + 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** `W31` for a weekly bucket, which is keyed by the `YYYY-MM-DD` of its Monday. */
function formatWeekTick(mondayIso: string): string {
    const week = getIsoWeek(mondayIso);
    // getIsoWeek returns 0 for anything it cannot parse; showing the raw key beats
    // printing "W0".
    return week > 0 ? `W${week}` : mondayIso;
}

/**
 * `W31 · 27 Jul – 2 Aug` — the week number plus the span it covers.
 *
 * The axis trades the dates for a week number, so the tooltip has to put them back.
 * Without this the shortened axis would be the only thing naming the period, and a
 * reader could no longer tell which days a bar covered.
 */
export function formatWeekRange(mondayIso: string, locale: Locale): string {
    const pattern = getChartLabelDateFormat(locale);
    const from = formatDateByPattern(mondayIso, pattern, locale, mondayIso);
    const end = isoWeekEnd(mondayIso);
    const to = formatDateByPattern(end, pattern, locale, end);
    const week = getIsoWeek(mondayIso);
    const span = `${from} – ${to}`;
    return week > 0 ? `W${week} · ${span}` : span;
}

/**
 * `Oct 2` (`2. okt.`, `2 oct`): the browser's short month and day for a day key, for a chart
 * whose axis reads better without the chart pattern's trailing period. The key is a zone-less
 * calendar day, which `Date` reads as UTC midnight, so it is formatted in UTC too: in the
 * browser's zone, anywhere west of UTC would show it a day early.
 */
export function formatShortMonthDay(day: string, locale: string): string {
    const date = new Date(day);
    if (Number.isNaN(date.getTime())) return day;
    return Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
        date
    );
}

export interface XAxisScaleParams {
    locale: Locale;
    stacked: boolean;
    xAxisKind: MetricXAxisKind;
    theme: ChartTheme;
    categoryLabel: ((key: string) => string) | undefined;
    xTicks: MetricXTicks | undefined;
    /** The axis keys, for `keepEnds`, which pins the last label to the last key. */
    days: string[];
    /** See `MetricChartSpec.gridLines`. */
    gridLines?: 'dashed' | 'solid';
}

interface TickContext {
    getLabelForValue: (value: number) => string;
}

/**
 * Whether `keepEnds` labels the tick at `index`: always the first and the last, and between
 * them every `step`-th, so at most `maxTicks` labels spread evenly over the span.
 */
function keepsEndTick(index: number, tickCount: number, maxTicks: number): boolean {
    if (index === 0 || index === tickCount - 1) return true;
    const step = Math.max(1, Math.floor((tickCount - 1) / Math.max(1, maxTicks - 1)));
    return index % step === 0;
}

export function buildXAxisScale(params: XAxisScaleParams): MetricXScaleOptions {
    const { locale, stacked, xAxisKind, theme, categoryLabel, xTicks, days, gridLines } = params;
    const formatPattern = getChartLabelDateFormat(locale);
    const maxTicks =
        xTicks?.maxTicksLimit === undefined
            ? undefined
            : Math.max(1, Math.min(days.length, xTicks.maxTicksLimit));

    function formatKey(key: string): string {
        if (xTicks?.format) return xTicks.format(key);
        if (xAxisKind === 'category') return categoryLabel ? categoryLabel(key) : key;
        if (xAxisKind === 'week') return formatWeekTick(key);
        if (xAxisKind === 'hour') return formatWallClockHourLocalized(key, locale);
        return formatDateByPattern(key, formatPattern, locale, key);
    }

    return {
        stacked,
        ...(gridLines === 'solid'
            ? { border: { color: theme.axis }, grid: { color: theme.grid } }
            : {
                  border: {
                      dash: CHART_GRID_DASH_PATTERN,
                      color: theme.axis,
                      width: CHART_GRID_LINE_WIDTH,
                  },
                  grid: {
                      color: theme.grid,
                      lineWidth: CHART_GRID_LINE_WIDTH,
                      ...CHART_AXIS_BORDER_MARGIN,
                  },
              }),
        ticks: {
            color: theme.text,
            ...(maxTicks === undefined ? {} : { maxTicksLimit: maxTicks }),
            ...(xTicks?.autoSkipPadding === undefined
                ? {}
                : { autoSkipPadding: xTicks.autoSkipPadding }),
            ...(xTicks?.padding === undefined ? {} : { padding: xTicks.padding }),
            callback: function (
                this: TickContext,
                tickValue: string | number,
                index: number,
                ticks: ReadonlyArray<{ value: number }>
            ): string | null {
                if (xTicks?.keepEnds && maxTicks !== undefined) {
                    if (!keepsEndTick(index, ticks.length, maxTicks)) return null;
                    // The last label names the last key even when Chart.js ends the ticks
                    // short of it, so the axis always reads to the end of its span.
                    if (index === ticks.length - 1 && days.length > 0) {
                        return formatKey(days[days.length - 1]);
                    }
                }
                return formatKey(this.getLabelForValue(Number(tickValue)));
            },
        },
    };
}
